import * as React from 'react';

import {Activity_Types} from 'nutshell-core/settings-constants';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {
    CallrailIcon,
    AircallIcon,
    RingcentralIcon,
    DialpadIcon,
    PhoneIcon,
    NutshellLogoMarkIcon,
    KixieIcon,
} from 'shells/icon';
import {Stack} from 'shells/layout/stack/stack';
import type {UpgradePillProps} from 'shells/limits/upgrade-pill-button';
import type {MessageType} from 'shells/message/message';
import {Body} from 'shells/typography/body';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {GetSessionUser_loggedInUser_phoneIntegrations as PhoneIntegration} from 'nutshell-graphql-types';

import {appHistory} from '../../history';

export function getFlashBannerType(isEnabled: boolean, errorMessage?: string | null): MessageType {
    if (errorMessage) {
        return 'warning';
    } else if (isEnabled) {
        return 'success';
    }

    return 'info';
}

export function getStatusText(
    integrationTypes: ExternalServiceType[],
    error?: string | null
): string {
    const enabledIntegrations = integrationTypes.map((integrationType) =>
        getServiceNoun(integrationType)
    );

    if (enabledIntegrations.length > 0) {
        if (error) {
            return `Connection problems: ${enabledIntegrations.join(', ')}`;
        }

        return `Enabled: ${enabledIntegrations.join(', ')}`;
    }

    return 'Call sync not enabled';
}

export function getStatusDescription(
    enabledIntegrationTypes: ExternalServiceType[],
    errorMessage?: string | null
): React.ReactElement<any> {
    if (errorMessage) {
        if (enabledIntegrationTypes.length === 1) {
            return (
                <div>
                    {/* @ts-expect-error */}
                    Your {getServiceNoun(enabledIntegrationTypes[0])} account is having connection
                    problems
                </div>
            );
        } else if (enabledIntegrationTypes.length > 1) {
            const serviceNames = enabledIntegrationTypes
                .map((integrationType) => getServiceNoun(integrationType))
                .join(', ');

            return (
                <div>Your phone integrations are having connection problems: {serviceNames}</div>
            );
        } else {
            return <div>Your phone integrations are having connection problems</div>;
        }
    }

    if (enabledIntegrationTypes.length === 0) {
        return <div>No phone integrations are currently connected</div>;
    }

    if (enabledIntegrationTypes.length === 1) {
        // @ts-expect-error
        const serviceName = getServiceNoun(enabledIntegrationTypes[0]);

        return <div>Your {serviceName} integration is enabled</div>;
    }

    const serviceNames = enabledIntegrationTypes
        .map((integrationType) => getServiceNoun(integrationType))
        .join(', ');

    return <div>Multiple phone integrations are enabled: {serviceNames}</div>;
}

export function getPopoverContent(input: {
    phoneIntegrations: PhoneIntegration[];
    errorMessage: string | null | undefined;
    setIsDialogOpen: (isOpen: boolean) => void;
    handleReconfigure: () => void;
}): React.ReactElement<any> {
    const {phoneIntegrations, errorMessage, setIsDialogOpen, handleReconfigure} = input;

    if (errorMessage) {
        return (
            <div className='pad-24'>
                {/* @ts-expect-error */}
                <Stack spacing={16} alignCenter={true}>
                    Calls will not begin syncing into Nutshell again until you’ve reconnected your
                    integrations.
                    <Button
                        variant='primary'
                        size='big'
                        onClick={() => {
                            handleReconfigure();
                            setIsDialogOpen(false);
                        }}
                    >
                        Reconnect
                    </Button>
                </Stack>
            </div>
        );
    }

    return (
        <Stack spacing={8}>
            {phoneIntegrations.map((integration) => (
                <div className='flex gap-8 pad-16' key={integration.integrationType}>
                    {getServiceTypeIconForStatusBar(integration.integrationType)}
                    <Body size='small'>
                        {getServiceNoun(integration.integrationType)} connected
                        {integration.externalUserIdentifier && (
                            <span> as {integration.externalUserIdentifier}</span>
                        )}
                    </Body>
                </div>
            ))}
        </Stack>
    );
}

export const getServiceTypeIconForStatusBar = (
    serviceType: ExternalServiceType
): React.ReactElement<any> => {
    switch (serviceType) {
        case 'CALLRAIL':
            return <CallrailIcon wrapWithDiv={false} size={20} />;
        case 'AIRCALL':
            return <AircallIcon wrapWithDiv={false} size={20} />;
        case 'RINGCENTRAL':
            return <RingcentralIcon wrapWithDiv={false} size={20} />;
        case 'DIALPAD':
            return <DialpadIcon wrapWithDiv={false} size={20} />;
        case 'KIXIE':
            return <KixieIcon wrapWithDiv={false} size={20} />;
        case 'CLICKTOCALL':
            return <NutshellLogoMarkIcon wrapWithDiv={false} size={20} fill={colors.orange} />;
        default:
            return <PhoneIcon wrapWithDiv={false} size={20} />;
    }
};

export const getServiceTypeIconForConnectionWidgets = (
    serviceType: ExternalServiceType
): React.ReactElement<any> => {
    switch (serviceType) {
        case 'CALLRAIL':
            return <CallrailIcon wrapWithDiv={false} size={24} />;
        case 'AIRCALL':
            return <AircallIcon wrapWithDiv={false} size={24} />;
        case 'RINGCENTRAL':
            return <RingcentralIcon wrapWithDiv={false} size={24} />;
        case 'DIALPAD':
            return <DialpadIcon wrapWithDiv={false} size={24} />;
        case 'KIXIE':
            return <KixieIcon wrapWithDiv={false} size={24} />;
        case 'CLICKTOCALL':
            return <NutshellLogoMarkIcon wrapWithDiv={false} size={40} fill={colors.orange} />;
        default:
            return <PhoneIcon wrapWithDiv={false} size={24} />;
    }
};

export const getServiceTypeIconComponent = (
    serviceType: ExternalServiceType
): React.ComponentType<any> => {
    switch (serviceType) {
        case 'CALLRAIL':
            return CallrailIcon;
        case 'AIRCALL':
            return AircallIcon;
        case 'RINGCENTRAL':
            return RingcentralIcon;
        case 'DIALPAD':
            return DialpadIcon;
        case 'KIXIE':
            return KixieIcon;
        case 'CLICKTOCALL':
            return NutshellLogoMarkIcon;
        default:
            return PhoneIcon;
    }
};

export const getServiceNoun = (serviceType: ExternalServiceType | 'KIXIE' | 'SKYPE'): string => {
    switch (serviceType) {
        case 'AIRCALL':
            return 'Aircall';
        case 'CALLRAIL':
            return 'CallRail';
        case 'RINGCENTRAL':
            return 'RingCentral';
        case 'CLICKTOCALL':
            return 'click-to-call';
        case 'KIXIE':
            return 'Kixie';
        case 'SKYPE':
            return 'Skype';
        default:
            return 'Dialpad';
    }
};

export const getServiceTypeFromString = (
    integrationType: string
): ExternalServiceType | null | undefined => {
    if (integrationType === 'AIRCALL') {
        return 'AIRCALL';
    } else if (integrationType === 'CALLRAIL') {
        return 'CALLRAIL';
    } else if (integrationType === 'RINGCENTRAL') {
        return 'RINGCENTRAL';
    } else if (integrationType === 'DIALPAD') {
        return 'DIALPAD';
    } else if (integrationType === 'KIXIE') {
        return 'KIXIE';
    } else if (integrationType === 'CLICKTOCALL') {
        return 'CLICKTOCALL';
    }

    return null;
};

export const getPreselectedPhoneIntegration = (
    phoneIntegrations: PhoneIntegration[],
    search: string
): PhoneIntegration | null | undefined => {
    const integrationTypeQueryParam = new URLSearchParams(search).get('integrationType') || '';
    const integrationType = getServiceTypeFromString(integrationTypeQueryParam);

    return integrationType
        ? phoneIntegrations.find(
              (phoneIntegration) => phoneIntegration.integrationType === integrationType
          )
        : null;
};

export const getServiceDescription = (serviceType: ExternalServiceType): string => {
    switch (serviceType) {
        case 'AIRCALL':
            return 'Call directly from Nutshell using Aircall’s desktop app';
        case 'CALLRAIL':
            return 'Use the CailRail desktop app to make calls and sync conversations with Nutshell';
        case 'RINGCENTRAL':
            return 'Use the RingCentral desktop or web app to make and log calls directly from Nutshell';
        case 'DIALPAD':
            return 'This works with the Dialpad desktop or web calling app installed on your computer';
        case 'KIXIE':
            return 'Logs your Kixie calls and voicemails directly into Nutshell';
        default:
            return 'Use this app to make and log calls directly from Nutshell';
    }
};

export const getActivityTypeSettingName = (
    integrationType: ExternalServiceType,
    isVoicemail: boolean
): string | null | undefined => {
    switch (integrationType) {
        case 'AIRCALL':
            return isVoicemail
                ? Activity_Types.DEFAULT_AIRCALL_VOICEMAIL_SETTING
                : Activity_Types.DEFAULT_AIRCALL_CALL_SETTING;
        case 'CALLRAIL':
            return isVoicemail
                ? Activity_Types.DEFAULT_CALLRAIL_VOICEMAIL_SETTING
                : Activity_Types.DEFAULT_CALLRAIL_CALL_SETTING;
        case 'RINGCENTRAL':
            return isVoicemail
                ? Activity_Types.DEFAULT_RINGCENTRAL_VOICEMAIL_SETTING
                : Activity_Types.DEFAULT_RINGCENTRAL_CALL_SETTING;
        case 'DIALPAD':
            return isVoicemail
                ? Activity_Types.DEFAULT_DIALPAD_VOICEMAIL_SETTING
                : Activity_Types.DEFAULT_DIALPAD_CALL_SETTING;
        case 'KIXIE':
            return isVoicemail
                ? Activity_Types.DEFAULT_KIXIE_VOICEMAIL_SETTING
                : Activity_Types.DEFAULT_KIXIE_CALL_SETTING;
        case 'CLICKTOCALL':
            return isVoicemail
                ? Activity_Types.DEFAULT_CLICK_TO_CALL_VOICEMAILS_ACTIVITY_TYPE_ID_SETTING
                : Activity_Types.DEFAULT_CLICK_TO_CALL_ACTIVITY_TYPE_ID_SETTING;
        default:
            return null;
    }
};

export const getClickToCallUpgradePillProps = (): UpgradePillProps => {
    return {
        size: 'small',
        onClick: () => {
            appHistory.push('/setup/billing/sales');
        },
        dataTip: `${getServiceNoun(
            'CLICKTOCALL'
        )} requires a pro plan or above. Click here to upgrade.`,
    };
};

export const getNextPageForGettingStarted = (
    integrationType?: ExternalServiceType | null
): string => {
    if (!integrationType) {
        return '/connect-phone-integration/choose-provider';
    }

    switch (integrationType) {
        case 'CLICKTOCALL':
            return '/connect-phone-integration/click-to-call/setup';
        default:
            return `/connect-phone-integration/choose-provider?integrationType=${integrationType}`;
    }
};
